import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Menu, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import logoImage from "@/assets/glow-up-logo-sm.webp";
import { useSectionNavigation } from "@/hooks/use-section-navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { navigateToSection, navigateHome } = useSectionNavigation();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2 md:gap-3">
            <img src={logoImage} alt="Glow Up LEADership Academy" width="126" height="80" fetchPriority="high" decoding="async" className="h-10 md:h-12 w-auto" />
            <div className="text-sm sm:text-base md:text-xl font-bold text-foreground leading-tight">Glow Up LEADership Academy</div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <button onClick={navigateHome} className="text-muted-foreground hover:text-foreground transition-colors">
              Home
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                Programs <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem asChild>
                  <button onClick={() => navigateToSection("#programs")} className="w-full text-left">
                    Programs Overview
                  </button>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/programs/mastermind-groups" onClick={(e) => {
                    if (window.location.pathname === '/programs/mastermind-groups') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}>Mastermind Groups</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/programs/coaching" onClick={(e) => {
                    if (window.location.pathname === '/programs/coaching') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}>Coaching</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/programs/organizational-leadership" onClick={(e) => {
                    if (window.location.pathname === '/programs/organizational-leadership') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}>Organizational Leadership</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/programs/maxwell-disc" onClick={(e) => {
                    if (window.location.pathname === '/programs/maxwell-disc') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}>Maxwell DISC</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/programs/workshops-lunch-learns" onClick={(e) => {
                    if (window.location.pathname === '/programs/workshops-lunch-learns') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}>Workshops & Lunch-and-Learns</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/programs/entrepreneur-sales" onClick={(e) => {
                    if (window.location.pathname === '/programs/entrepreneur-sales') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}>Entrepreneur & Sales Programs</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/programs/college-career" onClick={(e) => {
                    if (window.location.pathname === '/programs/college-career') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}>College & Career Programs</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={() => navigateToSection("#about")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              About
            </button>
            <button
              onClick={() => navigateToSection("#testimonials")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Testimonials
            </button>
            <button
              onClick={() => navigateToSection("#blog")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Blog
            </button>
            <button
              onClick={() => navigateToSection("#new-steps")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Next Steps
            </button>
            <Button variant="default" size="default" onClick={() => navigateToSection("#contact")}>
              Contact
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] z-[200] flex flex-col">
              <SheetTitle>Navigation Menu</SheetTitle>
              <SheetDescription>Access all pages and sections</SheetDescription>
              <nav className="flex flex-col gap-6 mt-8 overflow-y-auto flex-1 pb-8 pr-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => {
                      window.scrollTo({ top: 0, behavior: 'instant' });
                      if (window.location.pathname !== '/') {
                        navigateHome();
                      }
                    }, 300);
                  }}
                  className="text-lg text-foreground hover:text-primary transition-colors text-left cursor-pointer touch-manipulation active:scale-95 w-full"
                >
                  Home
                </button>
                <div className="text-lg font-semibold text-foreground mb-2">Programs</div>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => {
                      navigateToSection("#programs");
                    }, 300);
                  }}
                  className="text-base text-muted-foreground hover:text-primary transition-colors text-left pl-4 cursor-pointer touch-manipulation active:scale-95 w-full block mb-2"
                >
                  Programs Overview
                </button>
                <Link
                  to="/programs/mastermind-groups"
                  onClick={(e) => {
                    if (window.location.pathname === '/programs/mastermind-groups') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    setIsOpen(false);
                  }}
                  className="text-base text-muted-foreground hover:text-primary transition-colors text-left pl-4 cursor-pointer touch-manipulation active:scale-95 block"
                >
                  Mastermind Groups
                </Link>
                <Link
                  to="/programs/coaching"
                  onClick={(e) => {
                    if (window.location.pathname === '/programs/coaching') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    setIsOpen(false);
                  }}
                  className="text-base text-muted-foreground hover:text-primary transition-colors text-left pl-4 cursor-pointer touch-manipulation active:scale-95 block"
                >
                  Coaching
                </Link>
                <Link
                  to="/programs/organizational-leadership"
                  onClick={(e) => {
                    if (window.location.pathname === '/programs/organizational-leadership') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    setIsOpen(false);
                  }}
                  className="text-base text-muted-foreground hover:text-primary transition-colors text-left pl-4 cursor-pointer touch-manipulation active:scale-95 block"
                >
                  Organizational Leadership
                </Link>
                <Link
                  to="/programs/maxwell-disc"
                  onClick={(e) => {
                    if (window.location.pathname === '/programs/maxwell-disc') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    setIsOpen(false);
                  }}
                  className="text-base text-muted-foreground hover:text-primary transition-colors text-left pl-4 cursor-pointer touch-manipulation active:scale-95 block"
                >
                  Maxwell DISC
                </Link>
                <Link
                  to="/programs/workshops-lunch-learns"
                  onClick={(e) => {
                    if (window.location.pathname === '/programs/workshops-lunch-learns') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    setIsOpen(false);
                  }}
                  className="text-base text-muted-foreground hover:text-primary transition-colors text-left pl-4 cursor-pointer touch-manipulation active:scale-95 block"
                >
                  Workshops & Lunch-and-Learns
                </Link>
                <Link
                  to="/programs/entrepreneur-sales"
                  onClick={(e) => {
                    if (window.location.pathname === '/programs/entrepreneur-sales') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    setIsOpen(false);
                  }}
                  className="text-base text-muted-foreground hover:text-primary transition-colors text-left pl-4 cursor-pointer touch-manipulation active:scale-95 block"
                >
                  Entrepreneur & Sales Programs
                </Link>
                <Link
                  to="/programs/college-career"
                  onClick={(e) => {
                    if (window.location.pathname === '/programs/college-career') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    setIsOpen(false);
                  }}
                  className="text-base text-muted-foreground hover:text-primary transition-colors text-left pl-4 cursor-pointer touch-manipulation active:scale-95 block"
                >
                  College & Career Programs
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => {
                      navigateToSection("#about");
                    }, 300);
                  }}
                  className="text-lg text-foreground hover:text-primary transition-colors text-left cursor-pointer touch-manipulation active:scale-95 w-full"
                >
                  About
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => {
                      navigateToSection("#testimonials");
                    }, 300);
                  }}
                  className="text-lg text-foreground hover:text-primary transition-colors text-left cursor-pointer touch-manipulation active:scale-95 w-full"
                >
                  Testimonials
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => {
                      navigateToSection("#blog");
                    }, 300);
                  }}
                  className="text-lg text-foreground hover:text-primary transition-colors text-left cursor-pointer touch-manipulation active:scale-95 w-full"
                >
                  Blog
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => {
                      navigateToSection("#new-steps");
                    }, 300);
                  }}
                  className="text-lg text-foreground hover:text-primary transition-colors text-left cursor-pointer touch-manipulation active:scale-95 w-full"
                >
                  Next Steps
                </button>
                <Button
                  variant="default"
                  className="w-full mt-4"
                  onClick={() => {
                    setIsOpen(false);
                    setTimeout(() => {
                      navigateToSection("#contact");
                    }, 300);
                  }}
                >
                  Contact
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
